import Component from 'vue-class-component'
import {GridColumn} from '@payroll/grid-widget/src/models/GridColumn'
import {StoreNamespace} from '@/globals/StoreNamespaceGlobal'
import {Action} from 'vuex-class'
import {FieldType, SearchFieldModel} from '@payroll/search-results-widget/src/models/SearchField.model'
import FeedbackComponent from '../../base/FeedbackComponent'
import GridWidget from '@payroll/grid-widget/src/component/GridWidget.vue'
import {BaseRequest, EmployerRegistration, KeyValuePair, Notification} from '@payroll/optimus-common'

@Component({})
export default class SearchEmployerRegistrationsComponent extends FeedbackComponent {

    private readonly APPROVED_STATUS_CODE: string = '8'

    private searchFields: SearchFieldModel[] = []
    private columns: GridColumn[] = []
    private statuses: KeyValuePair[] = []
    private showStatusToggle: boolean = false
    private showModal: boolean = false
    private selectedEmployer: any = {}
    private userRegistrations: any[] = []
    private innerEmployers: any[] = []
    private modalServerErrorMessages: any[] = []
    private showConfirmModal: boolean = false
    private selectedRegistration!: EmployerRegistration

    @Action('searchEmployerRegistrations', StoreNamespace.SEARCH) searchEmployerRegistrations: any
    @Action('getEmployerRegistrationDetails', StoreNamespace.SEARCH) getEmployerRegistrationDetails: any
    @Action('deleteEmployerRegistration', StoreNamespace.SEARCH) deleteEmployerRegistration: any
    @Action('downloadEmployerRegistration', StoreNamespace.SEARCH) downloadEmployerRegistration: any
    @Action('approveRegistrations', StoreNamespace.SEARCH) approveRegistrations: any
    @Action('getEmployerRegistrationStatuses', StoreNamespace.SEARCH) getEmployerRegistrationStatuses: any

    mounted() {
        this.getEmployerRegistrationStatuses({requestorCallback: this})
        this.searchFields.push({
            label: 'by_employer_name',
            name: 'employer_name',
            property: 'employerName',
            fieldType: FieldType.TEXT_FIELD,
            maxLength: 30
        })
        this.searchFields.push({
            label: 'by_hp_number',
            name: 'hp_number',
            property: 'employerHp',
            fieldType: FieldType.NUMBER_FIELD,
            maxValue: 999999999
        })
        this.initColumns()

        if (this.statuses.length > 0) {
            this.loadEmployerRegistrations({
                pageNumber: 0,
                pageSize: GridWidget.DEFAULT_PAGE_SIZE
            })
        }
    }

    private initColumns(): void {
        this.columns.push({property: 'employerHp', header: 'hp_number'} as GridColumn)
        this.columns.push({property: 'employerName', header: 'employer_name'} as GridColumn)
        this.columns.push({property: 'phoneNum', header: 'phone_number'} as GridColumn)
        this.columns.push({property: 'dateCreated', header: 'registration_date'} as GridColumn)
    }

    loadEmployerRegistrations(searchObject: any) {
        this.showStatusToggle = !searchObject.status || searchObject.status.value !== this.APPROVED_STATUS_CODE
        this.searchEmployerRegistrations({
            requestBody: searchObject as BaseRequest,
            requestorCallback: this
        })
    }

    onRowClick(rowData: any) {
        this.innerEmployers = []
        this.userRegistrations = []
        this.modalServerErrorMessages = []
        this.selectedEmployer = rowData
        this.getEmployerRegistrationDetails({pathVariable: rowData.id, requestorCallback: this})
        this.showModal = true
    }

    onSuccess(data?: any, requestName?: any, message?: string) {
        switch (requestName) {
            case 'getEmployerRegistrationDetails':
                data.userRegistrations.forEach((userRegistration: any) => {
                    userRegistration.activeFlag = userRegistration.isMainUser || userRegistration.activeFlag
                })
                this.userRegistrations = data.userRegistrations
                for (let child of data.innerEmployers) {
                    let emp: any = {
                        value: child.value,
                        description: child.description,
                        activeFlag: child.filterFlag
                    }
                    this.innerEmployers.push(emp)
                }
                break
            case 'searchEmployerRegistrations':
                data.results.forEach((result: any) => {
                    result.fileType = 'PDF'
                    result.fileName = 'registration_' + result.employerHp
                })
                this.$emit('successResults', data)
                break
            case 'getEmployerRegistrationStatuses':
                this.statuses = data
                this.searchFields.push({
                    label: 'status',
                    property: 'status',
                    fieldType: FieldType.SELECT_FIELD,
                    selectOptions: this.statuses,
                    selectDisplay: 'description',
                    isRequired: true
                })
                this.loadEmployerRegistrations({
                    pageNumber: 0,
                    pageSize: GridWidget.DEFAULT_PAGE_SIZE
                })
                break
            case 'approveRegistrations':
                this.showModal = false
                let notificationObj: Notification = {
                    toShow: true,
                    message: "employer_registration_approved",
                    icon: "checkCircle",
                    secondsToDisappear: 3
                }
                this.$root.$emit('showAlertNotification', notificationObj)
                break
            case 'downloadRegistrationFormReport':
                this.$emit('successExport', data)
                break
            case 'deleteEmployerRegistration':
                this.showConfirmModal = false
                this.loadEmployerRegistrations({
                    pageNumber: 0,
                    pageSize: GridWidget.DEFAULT_PAGE_SIZE
                })
                break
        }
    }

    onError(messages: string[], code: number, requestName?: string): void {
        if ((requestName === 'getEmployerRegistrationDetails'
            || requestName === 'approveRegistrations'
            || requestName === 'deleteEmployerRegistration') && code == 400) {
            this.modalServerErrorMessages = messages
        } else {
            switch (code) {
                case 400:
                case 500:
                    this.serverErrorMessages = messages
                    break
                case 401:
                case 403:
                    this.$router.push({name: 'session-timeout'})
                    break
                case 404:
                    this.$router.push({name: 'not-found'})
                    break
                case 503:
                    this.$router.push({name: 'maintenance'})
                    break
                default:
                    this.$router.push({name: 'error'})
                    break
            }
        }
    }

    onExportClick(data: any) {
        this.downloadEmployerRegistration({pathVariable: data.id, requestorCallback: this})
    }

    onDeleteClick(data: any) {
        this.modalServerErrorMessages = []
        this.showConfirmModal = true
        this.selectedRegistration = data
    }

    onConfirmDeleteClick() {
        this.deleteEmployerRegistration({
            pathVariable: this.selectedRegistration.id,
            requestorCallback: this
        })
    }

    checkAllChildren(parent: any, checked: boolean) {
        for (let child of parent) {
            if (!child.isMainUser) {            //main user cannot be unchecked
                child.activeFlag = checked
            }
        }
    }

    onSaveResponse(): void {
        this.modalServerErrorMessages = []
        let approvedUsers: Array<any> = []
        let approvedEmployers: Array<any> = []
        for (let emp of this.innerEmployers) {
            if (emp.activeFlag)
                approvedEmployers.push(emp.value)
        }
        for (let user of this.userRegistrations) {
            if (user.activeFlag)
                approvedUsers.push(user.id)
        }
        if (approvedEmployers.length <= 0) {
            this.modalServerErrorMessages.push(this.$t('inner_employer_required'))
            return
        }
        if (approvedUsers.length <= 0) {
            this.modalServerErrorMessages.push(this.$t('user_required'))
            return
        }
        this.approveRegistrations({
            requestorCallback: this,
            requestBody: {
                employerRegistrationId: this.selectedEmployer.id,
                userRegistrationIds: approvedUsers,
                employerNums: approvedEmployers
            } as BaseRequest
        })
    }

    getSelectedRegistration(): { employerHp: number, employerName: string } {
        return {
            employerHp: this.selectedRegistration.employerHp,
            employerName: this.selectedRegistration.employerName
        }
    }
}
